import { Supplier, selectSupplierById, loadSupplier, loadSupplierStructure, SupplierRating } from "./suppliersSlice"
import Container from '@mui/material/Container'
import { Localized, useLocalization } from "@fluent/react"
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import { Box, Card, CardContent, Dialog, Divider, Fab, Grid, Paper, Rating, Skeleton, Stack, Tab, TableContainer, Typography } from "@mui/material";
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined'
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined'
import { showError } from "../notifications/notificationsSlice";
import { Link, useParams } from "react-router-dom";
import { AppId } from "../../app/appTypes";
import { LoadAreas } from "../../app/AppDataLoader";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { checkStructureNodes, selectStructure, uncheckAllStructureNodes, uncheckStructureNodes } from "../device/structureSlice";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { selectSettings } from "../settings/settingsSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { StructureTreeComponent } from "../device/Structure"
import { selectStructureRelationship } from "../device/structureRelationshipSlice"
import { If } from "../../app/If"
import { AddCollaborationOpinionDialog } from "./dialogs/AddCollaborationOpinionDialog"
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import AddIcon from '@mui/icons-material/Add';
import { SupplierHead } from "./components/SupplierHead"
import { DisplayRow } from "./components/DisplayRow"
import { ComputerizedSystemListForSupplier } from "../computerizedSystems/ComputerizedSystemListForSupplier"
import { DevicesForSupplier } from "../device/DevicesForSupplier"
import { UpdateSupplierStructureDialog } from "./dialogs/UpdateSupplierStructureDialog"
import EditIcon from '@mui/icons-material/Edit'
import { usePackage } from "../permissions/usePackage"

const PREFIX = 'SupplierForm';

const classes = {
    root: `${PREFIX}-root`,
    title: `${PREFIX}-title`,
    paper: `${PREFIX}-paper`,
    shortNameField: `${PREFIX}-shortNameField`,
    nameField: `${PREFIX}-nameField`,
    field: `${PREFIX}-field`
};

type FormTabs = "contact" | "areas" | "devices" | "computerizedSystems" | "collaboration"

type DialogState =
    | { type: "none" }
    | { type: "newCollaborationOpinion" }
    | { type: "updateStructure" }

type StructureState = "empty" | "loading" | "loaded" | "error"

export type SupplierFormProps = {
    entity: Supplier | undefined,
}

export const SupplierForm = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch();
    const { locale } = useAppSelector(selectSettings)
    const { id: paramId } = useParams(); const id = paramId ?? ''
    const entity = useAppSelector(state => selectSupplierById(state, id));
    const selectedStructureIds = useAppSelector(state =>
        Object.entries(selectStructure(state).tree.multiselection)
            .filter(([, selected]) => selected === true)
            .map(([structureId]) => structureId)
    )
    const [tab, setTab] = useState<FormTabs>("contact");
    const [dialog, setDialog] = useState<DialogState>({ type: "none" })
    const [structure, setStructure] = useState<StructureState>("empty")
    const [opinions, setOpinions] = useState<SupplierRating[]>(entity?.rating ?? [])

    const DialogContentDispatcher = ({ state: dialogState, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
        const onSuccess = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        const onClose = useCallback(() => {
            setter({ type: "none" })
        }, [setter])

        switch (dialogState.type) {
            case "none":
                return null
            case "newCollaborationOpinion":
                return (
                    <AddCollaborationOpinionDialog
                        supplierId={id}
                        onSuccess={(opinion: SupplierRating) => {
                            setOpinions((prev) => [opinion, ...prev])
                            onSuccess()
                        }}
                        onCancel={onClose}
                    />
                )
            case "updateStructure":
                return (
                    <UpdateSupplierStructureDialog
                        supplierId={id}
                        selectedStructureIds={selectedStructureIds}
                        onClose={onClose}
                        onSuccess={onSuccess}
                    />
                )
        }
    }

    const rel = useAppSelector(selectStructureRelationship)
    const handleTreeNodeChecked = useCallback((nodeId: AppId) => {
        // if some non-leaf node is checked all children must be checked also
        const relation = rel.entities[nodeId]
        if (relation) {
            dispatch(checkStructureNodes(relation.succs.map(i => i.structureId)))
        }
    }, [rel, dispatch])
    const handleTreeNodeUnchecked = useCallback((nodeId: AppId) => {
        // if non-leaf node is unchecked all children must be unchecked also
        const relation = rel.entities[nodeId]
        if (relation) {
            dispatch(uncheckStructureNodes(relation.succs.map(i => i.structureId)))
        }
    }, [rel, dispatch])

    useEffect(() => {
        if (!id || entity) {
            return
        }

        dispatch(loadSupplier(id)).catch(() => {
            dispatch(showError("error"))
        })
    }, [dispatch, entity, id])

    useEffect(() => {
        const load = async (supplierId: AppId) => {
            return await dispatch(loadSupplierStructure(supplierId)).unwrap()
        }
        if (tab === "areas" && structure === "empty") {
            setStructure("loading")
            load(id).then(x => {
                dispatch(uncheckAllStructureNodes())
                dispatch(checkStructureNodes(x.map(({ structureId }) => structureId)))
                setStructure("loaded")
            }).catch(() => {
                setStructure("error")
            })

        }
    }, [dispatch, tab, structure, id])

    useEffect(() => {
        setOpinions(entity?.rating ?? [])
    }, [entity?.rating])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const collaborationColumns: GridColDef<SupplierRating>[] = [
        {
            field: "createdBy",
            headerName: l10n.getString("author", null, "_Autor"),
            minWidth: 140,
            flex: 0.9,
        },
        {
            field: "createDate",
            headerName: l10n.getString("date", null, "_Data"),
            minWidth: 130,
            flex: 0.8,
            valueGetter: (_, row) => new Date(row.createDate).toLocaleString()
        },
        {
            field: "stars",
            headerName: l10n.getString("rating", null, "_Ocena"),
            minWidth: 170,
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                    <Rating readOnly value={params.row.rating} max={5} size="small" />
                    <Typography variant="body2" color="text.secondary">{params.row.rating.toFixed(1)}</Typography>
                </Stack>
            )
        },
        {
            field: "description",
            headerName: l10n.getString("description", null, "_Opis"),
            minWidth: 260,
            flex: 2,
        },
    ]

    const handleTabChange = useCallback((_, newTab: FormTabs) => {
        setTab(newTab)
    }, [])

    const canUpdate = usePackage("CompanySupplierDictionary", "ReadWrite")

    const handleAddOpinion = useCallback(() => {
        setDialog({ type: "newCollaborationOpinion" })
    }, [])

    return (
        <LoadAreas component={<Skeleton height="50vh" variant="rectangular" />}>
            {entity && <SupplierHead supplier={{ ...entity, rating: opinions }} />}
            <Container component={Paper} maxWidth={false} sx={{ paddingTop: 0 }}>
                <TabContext value={tab}>
                    <TabList onChange={handleTabChange} sx={{ marginTop: 0 }}>
                        <Tab
                            key={0}
                            value={"contact"}
                            label={<Localized id="companies-contact-data">_Dane Kontaktowe</Localized>} />
                        <Tab
                            key={1}
                            value={"areas"}
                            label={<Localized id="companies-areas">_Obszary działalności</Localized>} />
                        <Tab
                            key={2}
                            value={"devices"}
                            label={<Localized id="menu-devices">_Urządzenia</Localized>} />
                        <Tab
                            key={3}
                            value={"computerizedSystems"}
                            label={<Localized id="menu-computerized-systems">_Systemy komputerowe</Localized>} />
                        <Tab
                            key={4}
                            value={"collaboration"}
                            label={<Localized id="supplier-collaboration-ratings">_Ocena współpracy</Localized>} />

                    </TabList>

                    <TabPanel value="contact" sx={{ padding: 1 }}>
                        <Stack direction="row" spacing={3}>
                            <Grid container item xs={6} direction="column">
                                <Card className={classes.root} variant="outlined">
                                    <CardContent>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            sx={(t) => ({
                                                mb: 1,
                                                bgcolor: "grey.100",
                                                p: 1,
                                                borderRadius: 1,
                                                border: `1px solid ${t.palette.divider}`,
                                            })}
                                        >
                                            <HomeWorkOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                <Localized id="companies-address-data">_Dane adresowe</Localized>
                                            </Typography>
                                        </Stack>
                                        <Divider sx={{ mb: 1.5 }} />

                                        <DisplayRow label={<Localized id="companies-address-first">_Adres 1</Localized>} value={entity?.address1} />
                                        <DisplayRow label={<Localized id="companies-address-second">_Adres 2</Localized>} value={entity?.address2} />
                                        <DisplayRow label={<Localized id="companies-address-city">_Miasto</Localized>} value={entity?.city} />
                                        <DisplayRow label={<Localized id="companies-address-state">_Stan/Region</Localized>} value={entity?.state} />
                                        <DisplayRow label={<Localized id="companies-address-zip-code">_Zip/Postal code</Localized>} value={entity?.zipcode} />
                                        <DisplayRow label={<Localized id="companies-address-country">_Country</Localized>} value={entity?.country} />
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid container item xs={6} direction="column" >
                                <Card className={classes.root} variant="outlined">
                                    <CardContent>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            sx={(t) => ({
                                                mb: 1,
                                                bgcolor: "grey.100",
                                                p: 1,
                                                borderRadius: 1,
                                                border: `1px solid ${t.palette.divider}`,
                                            })}
                                        >
                                            <ContactPhoneOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                <Localized id="companies-contact-data">_Dane kontaktowe</Localized>
                                            </Typography>
                                        </Stack>
                                        <Divider sx={{ mb: 1.5 }} />

                                        <DisplayRow label={<Localized id="companies-contact-person">_Osoba kontaktowa</Localized>} value={entity?.contactPerson} />
                                        <DisplayRow label={<Localized id="companies-email">_Email</Localized>} value={entity?.email} />
                                        <DisplayRow label={<Localized id="companies-phone">_Tel</Localized>} value={entity?.phone} />
                                        <DisplayRow label={<Localized id="companies-mobile">_Mobile</Localized>} value={entity?.mobile} />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Stack>
                    </TabPanel>

                    <TabPanel value="areas" sx={{ padding: 1 }}>
                        <Box sx={{ maxHeight: "calc(100vh - 425px)", overflowY: "auto" }}>
                            <If condition={structure === "loaded"} otherwise={<Skeleton height={200} />}>
                                <StructureTreeComponent
                                    selectable={false}
                                    editable={false}
                                    multiselect={true}
                                    showCounter={true}
                                    onNodeChecked={handleTreeNodeChecked}
                                    onNodeUnchecked={handleTreeNodeUnchecked}
                                />
                            </If>
                        </Box>
                    </TabPanel>

                    <TabPanel value="devices" sx={{ padding: 1 }}>
                        <DevicesForSupplier supplierId={id} />
                    </TabPanel>

                    <TabPanel value="computerizedSystems" sx={{ padding: 1 }}>
                        <ComputerizedSystemListForSupplier supplierId={id} />
                    </TabPanel>

                    <TabPanel value="collaboration" sx={{ padding: 1 }}>
                        <Stack spacing={2}>
                            <TableContainer component={Paper}>
                                <DataGrid
                                    style={{ minHeight: 100, height: "calc(100vh - 475px)" }}
                                    rows={opinions}
                                    columns={collaborationColumns}
                                    localeText={getCustomLocaleText()}
                                    disableRowSelectionOnClick
                                    initialState={{
                                        sorting: {
                                            sortModel: [{ field: "createDate", sort: "desc" }],
                                        },
                                    }}
                                    sx={{ border: 0 }}
                                />
                                <Box sx={{ display: "flex" }}>
                                    {canUpdate && <Fab sx={{ margin: 1 }} onClick={handleAddOpinion} color="secondary" size="small">
                                        <AddIcon />
                                    </Fab>}
                                </Box>
                            </TableContainer>
                        </Stack>
                    </TabPanel>
                </TabContext>

                <Box sx={{ paddingBottom: 2, marginTop: 1 }}>
                    <If condition={tab === "areas"} otherwise={
                        <Button component={Link} to="/supplier" color="secondary" variant="outlined" size="small">
                            <Localized id="back">_Wróć</Localized>
                        </Button>
                    }>  
                        <ButtonGroup>
                            <Button component={Link} to="/supplier" color="secondary" variant="outlined" size="small">
                                <Localized id="back">_Wróć</Localized>
                            </Button>
                            {canUpdate && <Button color="primary" variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setDialog({ type: "updateStructure" })}>
                                <Localized id="change">_Zmień</Localized>
                            </Button>}
                        </ButtonGroup>
                    </If>
                </Box>

                <Dialog open={dialog.type !== "none"} fullWidth maxWidth="md" keepMounted={false}>
                    <DialogContentDispatcher state={dialog} setter={setDialog} />
                </Dialog>
            </Container>
        </LoadAreas>
    );
}